import React from 'react';
import { Button } from '@/components/ui/button';
import { Eraser, PenLine } from 'lucide-react';

/**
 * Canvas-based signature pad. Emits a `data:image/png;base64,…` string via onChange
 * whenever the user finishes a stroke. Consumer decides when to submit.
 *
 * Usage:
 *   <SignaturePad value={sig} onChange={setSig} />
 */
export default function SignaturePad({ value, onChange, disabled }) {
  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);
  const lastPoint = React.useRef(null);
  const [isEmpty, setIsEmpty] = React.useState(!value);

  const getCtx = () => canvasRef.current?.getContext('2d');

  React.useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // HiDPI: multiply backing store to keep the line crisp
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.floor(rect.width * dpr);
    c.height = Math.floor(rect.height * dpr);
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1A3A5C';
  }, []);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    lastPoint.current = point(e);
  };

  const move = (e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const p = point(e);
    const ctx = getCtx();
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPoint.current = p;
    if (isEmpty) setIsEmpty(false);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (canvasRef.current) {
      onChange?.(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = getCtx();
    ctx.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
    onChange?.('');
  };

  return (
    <div className="relative rounded-md border border-slate-200 bg-white" data-testid="signature-pad">
      <canvas
        ref={canvasRef}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: '100%', height: 160, cursor: disabled ? 'not-allowed' : 'crosshair', touchAction: 'none' }}
        data-testid="signature-canvas"
      />
      {isEmpty && !disabled && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-300">
          <PenLine className="mr-2 h-4 w-4" />
          <span className="text-xs">Draw your signature here</span>
        </div>
      )}
      {!disabled && (
        <div className="absolute right-2 top-2">
          <Button
            data-testid="signature-clear"
            type="button"
            variant="outline"
            size="sm"
            onClick={clear}
            className="h-7 gap-1 border-slate-200 text-[10px] text-slate-600"
          >
            <Eraser className="h-3 w-3" /> Clear
          </Button>
        </div>
      )}
    </div>
  );
}
